import { UseAlertsReturn, WorkspaceUsageAlert } from "../../../state/types";
import moment from "moment";
import { userHasPermission } from "../../../clientServices/permissions";
import {
    overagesAutoEnabled,
    isEnterprisePlan,
    firstFailedInvoicePaymentDate
} from "../../routes/workspace/utils";
import { getTitleForWorkspacePlan } from "../../routes/workspace/utils";
import globalStore from "../../../state";
import { useStore } from "zustand";
import useLocalStorage from "./useLocalStorage";
import { getIncludedCredits } from "../../utils/ubpUtils";
import checkUsage from "../../checkUsage";
import { Plan } from "@functions/plans/types";
import { useSignupCreditsStatus } from "../components/AlertManagers/Alerts/SignupCreditsAlert/useSignupCreditsStatus";

type WorkspaceUsageAlertConfiguration = { [key: string]: { muteUntil: number } };

export const INCLUDED_CREDITS_ALERT_THRESHOLD_FRACTION = 0.8;

export function useWorkspaceAlerts(): UseAlertsReturn<WorkspaceUsageAlert> {
    const {
        usage,
        subscription,
        freeTrainGpuExperiment,
        activePromos,
        workspaceId,
        planHistoricalUsageData
    } = useStore(globalStore, (state) => ({
        usage: state.getUsage(),
        subscription: state.currentWorkspaceId
            ? state.paidPlanSubscriptionById[state.currentWorkspaceId]?.data
            : undefined,
        freeTrainGpuExperiment: state.getExperimentState("freeTrainGpu"),
        activePromos: state.activePromos,
        workspaceId: state.currentWorkspaceId,
        planHistoricalUsageData:
            state.workspacePlanHistoricalUsageDataById[state.currentWorkspaceId]
    }));

    const [alertConfig, setAlertConfig] = useLocalStorage<WorkspaceUsageAlertConfiguration>(
        `workspaceUsageAlerts-${workspaceId}`,
        {}
    );

    const signupCreditsStatus = useSignupCreditsStatus(workspaceId);

    let alerts: WorkspaceUsageAlert[] = [];

    switch (usage?.plan) {
        case Plan.LegacySandbox:
            alerts.push(getImageUsage(usage));
            alerts.push(getTransformsUsage(usage));
            alerts.push(getTrainUsage(usage));
            alerts.push(getSaleUsage(activePromos));
            alerts.push(getInferenceUsage(usage));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.Public:
            alerts.push(getTrainUsage(usage));
            alerts.push(getSaleUsage(activePromos));
            alerts.push(getInferenceUsage(usage));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.Research:
            alerts.push(getTrainUsage(usage));
            alerts.push(getSaleUsage(activePromos));
            alerts.push(getInferenceUsage(usage));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.LegacyStarter:
            alerts.push(getReactivatePaidPlanAlert(usage, subscription));
            alerts.push(getImageUsage(usage));
            alerts.push(getTrialUsage(usage));
            alerts.push(getInferenceUsage(usage));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.UbpPremiumTrial:
            alerts.push(getTrialUsage(usage));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.UbpGrowth:
            alerts.push(getReactivatePaidPlanAlert(usage, subscription));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.UbpBasic:
            alerts.push(getReactivatePaidPlanAlert(usage, subscription));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.UbpCore:
            alerts.push(getReactivatePaidPlanAlert(usage, subscription));
            alerts.push(getUnpaidInvoiceAlert(usage));
            alerts.push(getIncludedCreditsAlert(usage, planHistoricalUsageData));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
            break;
        case Plan.Enterprise:
        case Plan.EnterpriseGrowth:
            alerts.push(getImageUsage(usage));
            alerts.push(getTransformsUsage(usage));
            alerts.push(getInferenceUsage(usage));
            alerts.push(getFreeTrainGpuAlert(freeTrainGpuExperiment));
        default:
            break;
    }

    alerts.push(getSignupCreditsAlert(signupCreditsStatus));
    alerts.push(getCustomAlert(usage));

    const now = Date.now();

    return {
        alerts: alerts
            .filter((alert) => {
                if (alertConfig?.[`${alert?.type}-${alert?.group}`]?.muteUntil > now) {
                    return false;
                }
                if (alert.enabled !== undefined) {
                    return alert.enabled;
                } else if (
                    alert.soft !== undefined &&
                    alert.limit !== undefined &&
                    alert.used !== undefined
                ) {
                    return !alert.soft && alert.used >= alert.limit;
                } else {
                    throw new Error("Invalid alert configuration");
                }
            })
            .reduce((acc: WorkspaceUsageAlert[], alert) => {
                const existingAlert = acc.find(
                    (existingAlert) => existingAlert.type === alert.type
                );
                if (existingAlert) {
                    existingAlert.label += `,${alert.label}`;
                    existingAlert.group += `-${alert.group}`;
                } else {
                    acc.push(alert);
                }
                return acc;
            }, []),
        muteAlert: ({
            alert,
            duration
        }: {
            alert: WorkspaceUsageAlert;
            duration: number;
        }): void => {
            const muteUntil = Date.now() + duration;
            setAlertConfig({ ...alertConfig, [`${alert.type}-${alert.group}`]: { muteUntil } });
        }
    };
}

function getUnpaidInvoiceAlert(usage): WorkspaceUsageAlert {
    const enabled = !!firstFailedInvoicePaymentDate(usage);
    return {
        type: "unpaid-invoice" as const,
        group: "unpaid-invoice" as const,
        level: "error" as const,
        enabled,
        label: "Unpaid Invoice"
    };
}

function getIncludedCreditsAlert(usage, planHistoricalUsageData) {
    const creditSystem = checkUsage.hasCreditSystem(usage);
    const includedCredits = getIncludedCredits({ usage });
    return {
        type: "included-credits" as const,
        group: "included-credits" as const,
        label: "Included Credits",
        level: "warning" as const,
        iconGlyph: "success" as const,
        enabled: creditSystem ? undefined : false,
        used: Number(planHistoricalUsageData?.totalCredits),
        limit: includedCredits * INCLUDED_CREDITS_ALERT_THRESHOLD_FRACTION,
        soft: false
    };
}

function getFreeTrainGpuAlert(freeTrainGpuExperiment) {
    return {
        type: "free-train-gpu" as const,
        group: "train" as const,
        label: "Free Train GPU",
        level: "info" as const,
        used: freeTrainGpuExperiment.enabled ? 1 : 0,
        limit: 1,
        soft: false
    };
}

function getImageUsage(usage) {
    return {
        type: "quota" as const,
        group: "images" as const,
        level: "warning" as const,
        label: "images",
        used: Number(usage?.images?.sources),
        limit: Number(usage?.limit?.sources),
        soft: Boolean(usage?.limit?.soft)
    };
}

function getInferenceUsage(usage) {
    return {
        type: "quota" as const,
        group: "inferences" as const,
        level: "warning" as const,
        label: "hosted inferences",
        used: Number(usage?.inference?.used),
        limit: Number(usage?.inference?.quota),
        soft:
            !isEnterprisePlan(usage) && // always show alert for enterprise plan
            (overagesAutoEnabled(usage) || Boolean(usage?.inference?.soft))
    };
}

/**
 * Calculate trial timing information from usage data
 */
export function getTrialTimingInfo(usage) {
    const endDateSeconds = usage?.trial?.endDate?.seconds ?? usage?.trial?.endDate?._seconds;
    const endDateMs = endDateSeconds * 1000;
    const endDate = new Date(endDateMs);
    const now = moment();

    const daysRemaining = moment(endDate).diff(now, "days", true); // true = fractional
    const isExpired = daysRemaining < 0;
    const isLastDay = now.isSame(endDate, "day");

    return {
        daysRemaining,
        isExpired,
        isLastDay,
        endDate,
        endDateFormatted: moment(endDate).format("LL"),
        endTimeFormatted: moment(endDate).format("LT"),
        timeTilEndHumanized: moment.duration(now.diff(endDate)).humanize()
    };
}

export function getMessage(usage) {
    const { daysRemaining, isLastDay, endTimeFormatted, endDateFormatted, timeTilEndHumanized } =
        getTrialTimingInfo(usage);
    const planTitle = getTitleForWorkspacePlan(usage?.plan, true);

    if (isLastDay) {
        return `Your ${planTitle} will end today at ${endTimeFormatted}`;
    } else if (daysRemaining > 0) {
        return `Your ${planTitle} will end in ${timeTilEndHumanized}`;
    }
    return `Your ${planTitle} ended on ${endDateFormatted}.`;
}

function getTrialUsage(usage) {
    if (usage?.trial?.isTrial) {
        const { daysRemaining, isExpired, isLastDay } = getTrialTimingInfo(usage);

        // Determine alert level based on days remaining
        const roundedDays = Math.ceil(daysRemaining);
        const isUrgent = roundedDays <= 3;
        const level = isUrgent ? "error" : "info";

        return {
            type: "trial" as const,
            group: "trial" as const,
            level: level as "error" | "info",
            label: "Trial Plan",
            message: getMessage(usage),
            used: 1,
            limit: 1,
            soft: false,
            // New trial-specific fields
            daysRemaining,
            isExpired,
            isLastDay
        };
    } else {
        return {
            type: "trial" as const,
            group: "trial" as const,
            level: "success" as const,
            label: "Trial Plan",
            message: ``,
            used: 0,
            limit: 1,
            soft: false,
            daysRemaining: undefined,
            isExpired: false,
            isLastDay: false
        };
    }
}

function getSaleUsage(activePromos) {
    const BLACK_FRIDAY_DEALS_ACTIVE = activePromos?.includes("black-friday");

    if (BLACK_FRIDAY_DEALS_ACTIVE) {
        return {
            type: "sale" as const,
            group: "sale" as const,
            level: "info" as const,
            label: "Sale Active",
            message: `Sale Active`,
            used: 1,
            limit: 1,
            soft: false
        };
    } else {
        return {
            type: "sale" as const,
            group: "sale" as const,
            level: "success" as const,
            label: "Sale Active",
            message: ``,
            used: 0,
            limit: 1,
            soft: false
        };
    }
}

function getTransformsUsage(usage) {
    return {
        type: "quota" as const,
        group: "transforms" as const,
        level: "warning" as const,
        label: "transforms",
        used: Number(usage?.images?.transforms),
        limit: Number(usage?.limit?.transforms),
        soft: Boolean(usage?.limit?.soft)
    };
}

function getTrainUsage(usage) {
    return {
        type: "quota" as const,
        group: "train" as const,
        level: "warning" as const,
        label: "training credits",
        used: Number(usage?.train?.used),
        limit: Number(usage?.train?.quota),
        soft: Boolean(usage?.limit?.soft)
    };
}

function getReactivatePaidPlanAlert(usage, subscription) {
    const hasBillingAccess = userHasPermission("manage_billing");
    if (
        subscription &&
        subscription.isActive &&
        subscription.cancellationPending &&
        hasBillingAccess
    ) {
        const planTitle = getTitleForWorkspacePlan(usage?.plan);
        const expirationDate = moment(subscription.cancelAt * 1000);
        return {
            type: "reactivate" as const,
            group: "reactivate" as const,
            level: "info" as const,
            label: "Reactivate Starter Plan",
            message: `Your ${planTitle} will expire on ${expirationDate.format("MMMM D, YYYY")}`,
            used: 1,
            limit: 1,
            soft: false
        };
    } else {
        return {
            type: "trial" as const,
            group: "trial" as const,
            level: "success" as const,
            label: "Active Starter Plan",
            message: ``,
            used: 0,
            limit: 1,
            soft: false
        };
    }
}

function getSignupCreditsAlert(
    status: { eligible: boolean; redeemed: boolean; rewardCents: number } | null
): WorkspaceUsageAlert {
    const enabled = !!(status && status.eligible && !status.redeemed);
    return {
        type: "signup-credits" as const,
        group: "signup-credits" as const,
        level: "info" as const,
        label: "Signup Credits",
        enabled,
        rewardCents: status?.rewardCents ?? 2000
    };
}

function getCustomAlert(usage): WorkspaceUsageAlert {
    const customAlert = usage?.customAlert;
    const enabled = !!(customAlert?.enabled && customAlert?.message);
    const level = ["warning", "error", "info", "success"].includes(customAlert?.level)
        ? customAlert.level
        : "info";

    return {
        type: "custom-alert" as const,
        group: "custom-alert" as const,
        level,
        enabled,
        message: customAlert?.message || "",
        label: "Custom Alert",
        linkUrl: customAlert?.linkUrl,
        linkText: customAlert?.linkText
    };
}
